import { Input, Textarea } from '@/layers/presentation/components/ui';
import { Autocomplete, AutocompleteItem } from '@/layers/presentation/components/ui/Autocomplete';
import { HandleParamChangeType } from '@/layers/presentation/hooks';
import logger from '@/layers/support/monitoring/logger';
import { formatParamOptionLabel } from '@/layers/support/utils/formatParamOptionLabel';
import { ParamType } from '@/types/modelEnums';
import { PatientParam, PatientParamValue, PatientParamValues } from '@/types/models';
import React, { useCallback, useMemo } from 'react';

const LOG_MODULE = 'FE [Presentation / Components / PatientParamsField]';

interface PatientParamsFieldProps {
  mode: 'filter' | 'profile' | 'programRequest';
  param: PatientParam;
  selectedOptions: PatientParamValues;
  onParamChange: HandleParamChangeType;
  isDisabled?: boolean;
  parentSelected?: boolean;
  patientParamsDictionary: PatientParam[];
}

interface HierarchicalParam extends PatientParam {
  children: HierarchicalParam[];
}

export default function PatientParamsField({
  mode,
  param,
  selectedOptions,
  onParamChange,
  isDisabled,
  parentSelected,
  patientParamsDictionary,
}: PatientParamsFieldProps) {
  const resetChildSelections = useCallback(
    (param: HierarchicalParam) => {
      logger.debug(`${LOG_MODULE} resetChildSelections`, { param });

      if (!param.children || param.children.length === 0) {
        return;
      }

      param.children.forEach(childParam => {
        if (selectedOptions[childParam.id]?.length > 0) {
          onParamChange({
            paramId: childParam.id,
            type: childParam.type,
            paramValue: [],
          });
        }
        resetChildSelections(childParam);
      });
    },
    [onParamChange, selectedOptions],
  );

  const organizeParams = useCallback((params: PatientParam[]): HierarchicalParam[] => {
    const paramMap: Record<number, HierarchicalParam> = {};
    const rootParams: HierarchicalParam[] = [];

    params.forEach(param => {
      paramMap[param.id] = { ...param, children: [] };
    });

    params.forEach(param => {
      if (param.parent_param_id && paramMap[param.parent_param_id]) {
        paramMap[param.parent_param_id].children.push(paramMap[param.id]);
      } else {
        rootParams.push(paramMap[param.id]);
      }
    });

    return rootParams;
  }, []);

  const isParentSelected =
    parentSelected &&
    Array.isArray(selectedOptions[param.id]) &&
    (selectedOptions[param.id]?.length > 0 || mode === 'filter');

  const filteredOptions = param.options.filter(
    option =>
      !option.parent_option_id ||
      (Array.isArray(selectedOptions[param.parent_param_id]) &&
        (selectedOptions[param.parent_param_id] as number[]).includes(option.parent_option_id)),
  );
  const isFilteredOptionsEmpty = filteredOptions.length === 0;

  const hierarchicalParams = useMemo(
    () => organizeParams(patientParamsDictionary),
    [patientParamsDictionary, organizeParams],
  );

  const handleChange = useCallback(
    async (paramId: number, paramValue: PatientParamValue) => {
      // Reset child parameter selections if the parent selection changed
      const param = hierarchicalParams.find(p => p.id === paramId);
      if (param) {
        resetChildSelections(param as HierarchicalParam);
      }

      onParamChange({
        paramId,
        type: param?.type,
        paramValue,
      });
    },
    [onParamChange, hierarchicalParams, resetChildSelections],
  );

  return (
    <React.Fragment key={param.id}>
      {(() => {
        /*
         * Previously we used `selectedKey` for the Select component. After switching to
         * `Autocomplete`, we no longer need this variable. Leaving the calculation here
         * would trigger the unused-variable rule, so it has been removed.
         */

        switch (param.type) {
          case ParamType.Select:
            const selectedOption = filteredOptions.find(
              opt => opt.id === selectedOptions[param.id]?.[0],
            );

            // Determine the display label for the currently selected option
            const selectedOptionLabel = selectedOption
              ? formatParamOptionLabel(param.slug, selectedOption.option)
              : undefined;

            return (
              <Autocomplete
                label={param.name}
                query={selectedOptionLabel}
                onSelectionChange={key => {
                  const keyStr = key?.toString();
                  handleChange(param.id, keyStr ? [Number(keyStr)] : []);
                }}
                onInputChange={value => {
                  if (value === '') {
                    handleChange(param.id, []);
                  }
                }}
                isDisabled={isDisabled || isFilteredOptionsEmpty}
                allowsCustomValue={false}
                allowsEmptyCollection
                isClearable
              >
                {filteredOptions.map(param_option => {
                  const displayLabel = formatParamOptionLabel(param.slug, param_option.option);

                  return (
                    <AutocompleteItem
                      key={param_option.id}
                      value={param_option.id}
                      textValue={param_option.option}
                    >
                      {displayLabel}
                    </AutocompleteItem>
                  );
                })}
              </Autocomplete>
            );
          case ParamType.Number:
            return (
              <Input
                label={param.name}
                type="number"
                value={selectedOptions[param.id]?.[0] ? String(selectedOptions[param.id][0]) : ''}
                onChange={e =>
                  handleChange(param.id, e.target.value ? [Number(e.target.value)] : [])
                }
                isDisabled={isDisabled || !!(param.parent_param_id && !isParentSelected)}
              />
            );
          case ParamType.Text:
          default:
            return (
              <Textarea
                label={param.name}
                type="text"
                value={
                  typeof selectedOptions[param.id] === 'string'
                    ? (selectedOptions[param.id] as string)
                    : ''
                }
                onChange={e => handleChange(param.id, e.target.value)}
                isDisabled={isDisabled || !!(param.parent_param_id && !isParentSelected)}
              />
            );
        }
      })()}
      {/* {param.children.length > 0 && renderParams(param.children, isParentSelected)} */}
    </React.Fragment>
  );
}
